import { css } from '@emotion/react';
import { FirstDataRenderedEvent, GetRowIdParams, GridReadyEvent } from 'ag-grid-community';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';
import { AgTable } from '../../../../../components/AgTable/AgTable';
import { KpiPeriod } from '../../../../../data-models/company-financials.data-model';
import { colors } from '../../../../../theme/colors';
import { companyProfileFinancialsDefaultColDef } from '../../../commonGridColDefs';
import { getEachDateByPeriodAndFrequency } from '../../../utils/financialUtils';
import { selectedEndDateFinancialsState } from '../../FinancialsDatePickers/useFinancialsDateSettings';
import { isHeaderCompParams, useFinancialsColumnDefs } from '../hooks/useFinancialsColumnDefs';
import { IFinancialsRowData2, useFinancialsGridData } from '../hooks/useFinancialsGridData';
import { useFinancialsGridRowHeight } from '../hooks/useFinancialsGridRowHeight';
import { PinnedYearColumnTag } from '../pinnedYears/usePinnedYearColDefs';
import { financialsGridApiState, useVisibleColumns } from '../pinnedYears/useVisibleColumns';
import { KpiType } from '../FinancialsTypeToggle';
import { useFinancialsGridSettings } from './FinancialsSettingsState';
import { useFinancialsSortState } from './financialsSortStateJ';
import { FinancialsGridNoRowsOverlay } from './FinancialsGridNoRowsOverlay';

const rowBg = `${colors.primary[0]}55`;

export const finTableStyles = css`
  margin-top: 0.5rem;
  height: 100%;
  --ag-borders: none;
  .ag-overlay {
    & .ag-react-container {
      background-color: transparent;
      pointer-events: auto; // for the "add financials & kpis" button
    }
  }
  .ag-root-wrapper {
    --ag-row-background-color: ${rowBg};
    --ag-odd-row-background-color: ${rowBg};
    --ag-background-color: ${rowBg};
    // we can remove this if we decide to pass columnBorder: true to theme
    .ag-cell {
      border-right: 1px solid ${colors.neutral[20]};
    }
    .ag-pinned-right-cols-container {
      background: ${colors.primary[20]}22;
    }
    .${PinnedYearColumnTag} {
      background: ${colors.primary[20]}88;
      &:hover {
        background-color: ${colors.primary[20]}99 !important;
      }
    }
  }
  .centered-header .ag-header-cell-label {
    width: 100%;
    justify-content: center;
    text-align: center;
  }
  .ag-row-level-1 .ag-cell.ag-cell-last-left-pinned {
    padding-left: 3.75rem; // indent first column to signify grouping when groupDisplayType='groupRows'
  }
`;

export type CompanyProfileFinancialsTableProps = {
  totalSelectedPeriods: number;
  frequency: KpiPeriod;
  fye: number;
};

export function CompanyProfileFinancialsTable({
  totalSelectedPeriods,
  frequency,
  fye,
}: CompanyProfileFinancialsTableProps) {
  const selectedEndDate = useAtomValue(selectedEndDateFinancialsState)!;
  const allPeriods = useMemo(
    () => getEachDateByPeriodAndFrequency({ totalSelectedPeriods, frequency, fye, endDate: selectedEndDate }),
    [totalSelectedPeriods, frequency, fye, selectedEndDate]
  );
  const [gridApi, setGridApi] = useAtom(financialsGridApiState);

  const onGridReady = useCallback(
    (e: GridReadyEvent) => {
      setGridApi(e.api);
    },
    [setGridApi]
  );
  const onGridPreDestroyed = useCallback(() => {
    setGridApi(null);
  }, [setGridApi]);

  const { onColumnVisible } = useVisibleColumns();
  const { displaySettings } = useFinancialsGridSettings();

  const { getFinancialsRowData } = useFinancialsGridData();

  const rowData = useMemo(
    () =>
      getFinancialsRowData({
        period: frequency,
        displaySettings,
        type: KpiType.Quantitative,
      }),
    [getFinancialsRowData, frequency, displaySettings]
  );

  const { columnDefs } = useFinancialsColumnDefs({
    allPeriods: allPeriods.dates,
    frequency,
    options: displaySettings,
  });

  const { getRowHeight } = useFinancialsGridRowHeight(gridApi, displaySettings);

  const getRowId = useCallback((params: GetRowIdParams<IFinancialsRowData2>) => {
    return String(params.data?.id);
  }, []);

  const { onSortChanged, resetSort } = useFinancialsSortState();
  useEffect(() => {
    return resetSort;
  }, [resetSort]);

  const onFirstDataRendered = useCallback((e: FirstDataRenderedEvent) => {
    const periodEntries = e.api.getAllDisplayedColumns().flatMap((col) => {
      const params = col.getColDef().headerComponentParams;
      if (col.isPinned() || !isHeaderCompParams(params)) {
        return [];
      }
      return [{ col, colDate: params.colDate }];
    });

    if (!periodEntries.length) {
      return;
    }

    const now = Date.now();
    const { col: targetColumn } = periodEntries.reduce((closest, entry) =>
      Math.abs(entry.colDate.getTime() - now) < Math.abs(closest.colDate.getTime() - now) ? entry : closest
    );

    e.api.ensureColumnVisible(targetColumn, 'end');
  }, []);

  return (
    <div css={finTableStyles}>
      <AgTable
        key={`${totalSelectedPeriods}-${frequency}`}
        columnDefs={columnDefs}
        defaultColDef={companyProfileFinancialsDefaultColDef}
        groupDefaultExpanded={-1}
        groupDisplayType='groupRows'
        rowData={rowData}
        rowGroupPanelShow='never'
        suppressMovableColumns={true}
        suppressRowDrag={true}
        suppressContextMenu={true}
        getRowHeight={getRowHeight}
        suppressScrollOnNewData
        suppressCsvExport
        suppressExcelExport
        getRowId={getRowId}
        onSortChanged={onSortChanged}
        suppressColumnMoveAnimation // prevent pinned menu popover from being misplaced
        onGridReady={onGridReady}
        onGridPreDestroyed={onGridPreDestroyed}
        onColumnVisible={onColumnVisible}
        onFirstDataRendered={onFirstDataRendered}
        noRowsOverlayComponent={FinancialsGridNoRowsOverlay}
      />
    </div>
  );
}
